import React, { useEffect } from 'react'
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useTRPC } from '@/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatDate } from '@/lib/date';
import { FormProvider, useFormContext } from '../../forms/form-editor/context';
import { Field } from '../../forms/form-editor/canvas';
import { FieldTypeName } from '../../forms/form-editor/fields';
import { TRPCClientError } from '@trpc/client';
import { toast } from 'sonner';
import * as Icons from "lucide-react"
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  task_id: string;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface SubmitTaskData {
  task_id: string;
  feedback: string | undefined;
  fields: Array<{ field_id: string; answer: string }>;
}

type FieldResponse = {
  fieldId: string;
  value: string;
  fieldType?: FieldTypeName | string;
};

type DraftData = {
  fieldResponses: FieldResponse[];
  feedback?: string;
  player?: { id: string; name: string };
};

type RecentEditedPlayer = {
  task_id: string;
  player_id: string;
  player_name: string;
};

const RECENT_EDITED_PLAYERS_KEY = ['recent-edited-players'];

const InnerFillTask = ({ task_id, trigger, onSuccess, open, onOpenChange }: Props) => {
  const { trpc } = useTRPC();
  const qc = useQueryClient();
  const [internalOpen, setInternalOpen] = React.useState(false);

  const { fieldResponses, isFormComplete, loadInitialFields, updateFieldResponse, loadInitialResponses } = useFormContext();
  const [feedback, setFeedback] = React.useState<string | undefined>(undefined);

  const isControlled = open !== undefined;
  const sheetOpen = isControlled ? !!open : internalOpen;
  const setSheetOpen = (v: boolean) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange?.(v);
  };

  const { data: task, isLoading: isLoadingTask } = useQuery({
    queryKey: ['tasks', 'byId', task_id],
    queryFn: async () => trpc.tasks.getById.query({ task_id }),
    enabled: sheetOpen && !!task_id,
   });

  const form = task?.report?.form;

  const draftKey = React.useMemo(() => ['tasks', 'byId', task_id, 'draft'] as const, [task_id]);
  const draftFilledRef = React.useRef(false);
  const lastTaskIdRef = React.useRef<string | null>(null);

React.useEffect(() => {
  if (lastTaskIdRef.current !== task_id) {
    loadInitialFields([]);
    loadInitialResponses([]);
    setFeedback(undefined);
    draftFilledRef.current = false;
    lastTaskIdRef.current = task_id;
  }

  if (!sheetOpen) return;
  if (!task || !form) return;
  if (draftFilledRef.current) return;

  loadInitialFields(
    form.fields.map(field => ({
      id: field.id,
      type: field.type as FieldTypeName,
      name: field.name ?? "",
      parent: field.parent_id ?? "",
      question: field.question ?? "",
      required: field.required ?? false,
      options:
        'options' in field
          ? field.options?.map(option => ({
              id: option.id,
              text: option.text ?? "",
            }))
          : [],
    }))
  );

  const draft = qc.getQueryData<DraftData>(draftKey);

  if (draft) {
    draft.fieldResponses.forEach(fr => {
      const inferredType =
        fr.fieldType ||
        form.fields.find(f => f.id === fr.fieldId)?.type ||
        'text';
      updateFieldResponse(fr.fieldId, fr.value, inferredType);
    });
    if (draft.feedback !== undefined) setFeedback(draft.feedback);
  }
    draftFilledRef.current = true;
  }, [sheetOpen, task_id, task, form, qc, draftKey]); 

    React.useEffect(() => {
    if (!sheetOpen) return;
    if (!task) return;

    if (task?.completed) {
      qc.removeQueries({ queryKey: draftKey, exact: true });
      qc.setQueryData<RecentEditedPlayer[]>(
        RECENT_EDITED_PLAYERS_KEY,
        (old) => (old ?? []).filter(e => e.task_id !== task_id)
      );
      return;
    }

    if (task?.mongo_player?.id && task?.mongo_player?.name) {
      const { id: playerId, name: playerName } = task.mongo_player!;
      qc.setQueryData<RecentEditedPlayer[]>(
        RECENT_EDITED_PLAYERS_KEY,
        (old) => {
          const list = old ?? [];
          const idx = list.findIndex(e => e.task_id === task_id);

          if (idx !== -1) {
            const copy = list.slice();
            copy[idx] = { ...copy[idx], player_name: playerName, player_id: playerId };
            return copy;
          }

          return [...list, { task_id, player_id: playerId, player_name: playerName }].slice(-25);
        }
      );
    }

    const hasContent =
      (fieldResponses && fieldResponses.length > 0) ||
      (feedback && feedback.trim() !== '');

    qc.setQueryData<DraftData>(draftKey, {
      fieldResponses: hasContent ? fieldResponses : [],
      feedback: hasContent ? feedback : undefined,
      player: task?.mongo_player
        ? { id: task.mongo_player.id, name: task.mongo_player.name }
        : undefined,
    });
  }, [
    sheetOpen,
    task,
    fieldResponses,
    feedback,
    qc,
    draftKey,
    task_id,
  ]);

  const { mutate: submitTask, isLoading: isSubmitting } = useMutation({
    mutationFn: async (data: SubmitTaskData) =>
      trpc.tasks.submit.mutate({
        task_id: data.task_id,
        feedback: data.feedback,
        fields: data.fields,
      }),
    onSuccess: () => {
      toast.success('Taak succesvol verzonden');

      qc.setQueryData(['tasks', 'byId', task_id], (old: any) =>
         old
           ? {
               ...old,
              feedback,
               localFieldResponses: fieldResponses,
               completed: true,
             }
           : old
     );

      qc.removeQueries({ queryKey: draftKey, exact: true });

      qc.setQueryData<RecentEditedPlayer[]>(
        RECENT_EDITED_PLAYERS_KEY,
        (old) => (old ?? []).filter(e => e.task_id !== task_id)
      );

      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['assigned-tasks'] });

      onSuccess?.();
      setSheetOpen(false);
    },
    onError: err => {
      if (err instanceof TRPCClientError || err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error('Er is een onbekende fout opgetreden.');
      }
    },
  });

  const handleSubmit = () => {
    if (!isFormComplete()) return;
    if (isSubmitting) return;

    const fields = fieldResponses.map(fr => ({
      field_id: fr.fieldId,
      answer: fr.value,
    }));

    submitTask({
      task_id,
      feedback,
      fields,
    });
  };

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent className="md:max-w-7xl">
            <SheetHeader>
                <SheetTitle>Taak aanvullen</SheetTitle>
                <SheetDescription>Vul de velden in om uw taak aan te vullen</SheetDescription>
            </SheetHeader>
            <div className='h-full overflow-auto px-4'>
                {isLoadingTask && <p className="text-center text-sm text-muted-foreground my-2">Bezig met laden...</p>}

                <div className="flex flex-col gap-4">
                    <div className='bg-muted p-4 rounded-lg border'>
                        <p className='text-sm font-medium text-muted-foreground uppercase'>{task?.report?.type?.join(' - ')}</p>
                        <p className="text-2xl font-bold">{task?.mongo_player?.name}</p>
                        {task?.note && <p className='text-sm font-medium text-foreground'>Notitie: {task?.note}</p>}
                    </div>

                    <div className='bg-muted p-2 rounded-lg border flex flex-col gap-8'>
                        {form?.fields?.map((field) => {
                            return (
                            <div
                                className="w-full overflow-hidden border rounded-md shadow-sm"
                                key={field.id}
                            >
                                <Field
                                    field={{
                                        id: field.id,
                                        name: field.name ?? "",
                                        type: field.type as FieldTypeName,
                                        required: field.required ?? false,
                                        parent: field.parent_id,
                                        question: field.question ?? "",
                                        options:
                                        "options" in field
                                            ? field.options?.map((option) => ({
                                                id: option.id,
                                                text: option.text ?? "",
                                            }))
                                            : [],
                                    }}
                                    preview
                                />
                            </div>
                            );
                        })}
                    </div>

                    <div className='bg-muted p-2 rounded-lg border flex flex-col gap-8'>
                        <div>
                            <Label>Feedback</Label>
                            <Textarea value={feedback} onChange={(e)=>{setFeedback(e.currentTarget.value)}} />
                        </div>
                    </div>
                </div>
            </div>
            <SheetFooter>
                <div className='flex items-center justify-end gap-2'>
                    <Button variant="outline" onClick={()=>{setSheetOpen(false)}}>Sluiten</Button>
                    <Button
                        onClick={()=>{handleSubmit()}}
                        disabled={!isFormComplete() || isSubmitting}
                    >
                        {isSubmitting && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                        Opslaan
                    </Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>
  )
}

const FillTask = (props: Props) => (
  <FormProvider>
    <InnerFillTask {...props} />
  </FormProvider>
);

export default FillTask;